import React, { useState } from "react";
import TopNav from "@/components/TopNav";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import DeleteAccountDialog from "@/components/DeleteAccountDialog";
import CaregiverProfileCard from "@/components/CaregiverProfileCard";

export const CHANGE_PW = {
    currentInput: "change-pw-current-input",
    newInput: "change-pw-new-input",
    confirmInput: "change-pw-confirm-input",
    submitBtn: "change-pw-submit-btn",
};

export default function ChangePassword() {
    const [form, setForm] = useState({ current: "", next: "", confirm: "" });
    const [busy, setBusy] = useState(false);

    async function submit(e) {
        e.preventDefault();
        if (form.next !== form.confirm) return toast.error("New passwords don't match");
        setBusy(true);
        try {
            await api.post("/auth/change-password", {
                current_password: form.current,
                new_password: form.next,
            });
            toast.success("Password updated");
            setForm({ current: "", next: "", confirm: "" });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="min-h-screen bg-stone-50">
            <TopNav />
            <main className="max-w-2xl mx-auto px-6 lg:px-8 py-10 animate-fade-in">
                <div className="mb-8">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-2">Account</div>
                    <h1 className="font-outfit text-3xl font-semibold text-stone-900 flex items-center gap-3">
                        <KeyRound className="w-6 h-6 text-emerald-700" /> Change password
                    </h1>
                </div>
                <Card className="rounded-2xl border-stone-200">
                    <CardContent className="p-8">
                        <form onSubmit={submit} className="space-y-4">
                            <div>
                                <Label>Current password</Label>
                                <Input
                                    data-testid={CHANGE_PW.currentInput}
                                    type="password"
                                    value={form.current}
                                    onChange={(e) => setForm({ ...form, current: e.target.value })}
                                    required
                                    className="mt-1.5 h-12 rounded-xl"
                                />
                            </div>
                            <div>
                                <Label>New password</Label>
                                <Input
                                    data-testid={CHANGE_PW.newInput}
                                    type="password"
                                    value={form.next}
                                    onChange={(e) => setForm({ ...form, next: e.target.value })}
                                    required
                                    minLength={8}
                                    className="mt-1.5 h-12 rounded-xl"
                                />
                            </div>
                            <div>
                                <Label>Confirm new password</Label>
                                <Input
                                    data-testid={CHANGE_PW.confirmInput}
                                    type="password"
                                    value={form.confirm}
                                    onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                                    required
                                    minLength={8}
                                    className="mt-1.5 h-12 rounded-xl"
                                />
                            </div>
                            <div className="flex justify-end pt-2">
                                <Button
                                    data-testid={CHANGE_PW.submitBtn}
                                    type="submit"
                                    disabled={busy}
                                    className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-8 h-11"
                                >
                                    {busy ? "Saving…" : "Update password"}
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>

                <CaregiverProfileCard />

                {/* Danger zone — permanent account deletion */}
                <div className="mt-10">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-red-700 mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-3.5 h-3.5" /> Danger zone
                    </div>
                    <Card className="rounded-2xl border-red-200">
                        <CardContent className="p-6 sm:p-8">
                            <h2 className="font-outfit text-xl font-semibold text-stone-900">Delete my account</h2>
                            <p className="text-sm text-stone-600 font-dmsans mt-1.5 leading-relaxed">
                                Permanently remove your profile from Infini HealthCare. Your name, email, phone and address will be anonymised;
                                saved cards will be detached from Stripe. Historical charges and tax records are kept for legal compliance
                                but no longer linked to you. This action cannot be undone.
                            </p>
                            <div className="mt-4">
                                <DeleteAccountDialog />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </main>
        </div>
    );
}
